//
// Copyright © 2020, 2021 Anticrm Platform Contributors.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import core, {
  Class,
  ClassifierKind,
  Data,
  Doc,
  DocumentQuery,
  DocumentUpdate,
  Hierarchy,
  IdMap,
  Ref,
  Status,
  StatusCategory,
  TxOperations,
  type AnyAttribute,
  type Rank,
  type RefTo
} from '@hcengineering/core'
import { PlatformError, getEmbeddedLabel, unknownStatus } from '@hcengineering/platform'
import task, { Project, ProjectStatus, ProjectType, Task, TaskType } from '.'
import { makeRank } from '@hcengineering/rank'

export { genRanks, makeRank } from '@hcengineering/rank'

/**
 * @deprecated Prefer {@link makeRank}
 *
 * TODO: Drop after everything migrates to {@link makeRank}
 */
export const calcRank = (prev?: { rank: Rank }, next?: { rank: Rank }): string => {
  return makeRank(prev?.rank, next?.rank)
}

/**
 * @public
 */
export function getProjectTypeStates (
  projectType: Ref<ProjectType> | undefined,
  types: IdMap<ProjectType>,
  statuses: IdMap<Status>
): Status[] {
  if (projectType === undefined) return []
  return (
    (types
      .get(projectType)
      ?.statuses?.map((p) => statuses.get(p._id))
      ?.filter((p) => p !== undefined) as Status[]) ?? []
  )
}

/**
 * @public
 */
export function getStates (space: Project | undefined, types: IdMap<ProjectType>, statuses: IdMap<Status>): Status[] {
  if (space === undefined) return []
  return getProjectTypeStates(space.type, types, statuses)
}

/**
 * @public
 */
export function getTaskTypeStates (
  taskType: Ref<TaskType> | undefined,
  types: IdMap<TaskType>,
  statuses: IdMap<Status>
): Status[] {
  if (taskType === undefined) return []
  return (
    (types
      .get(taskType)
      ?.statuses?.map((p) => statuses.get(p))
      ?.filter((p) => p !== undefined) as Status[]) ?? []
  )
}

/**
 * @public
 */
export function getStatusIndex (type: ProjectType, taskTypes: IdMap<TaskType>, status: Ref<Status>): number {
  return (
    type.tasks
      .map((it) => taskTypes.get(it))
      .flatMap((it) => it?.statuses.indexOf(status))
      .filter((it) => (it ?? 0) >= 0)
      .reduce((p, c) => (p ?? 0) + (c ?? 0), 0) ?? -1
  )
}

/**
 * @public
 *
 * Fetches all TaskTypes belonging to a ProjectType along with all Statuses used by those TaskTypes.
 * Used to build a mapping when changing a project's type or moving issues across differently-typed projects.
 */
export async function getProjectTypeTaskTypesAndStatuses (
  client: TxOperations,
  projectType: Ref<ProjectType>
): Promise<{ taskTypes: TaskType[], statuses: Status[] }> {
  const taskTypes = await client.findAll(task.class.TaskType, { parent: projectType })
  const statusIds = Array.from(new Set(taskTypes.flatMap((it) => it.statuses)))
  const statuses = await client.findAll(core.class.Status, { _id: { $in: statusIds } })
  return { taskTypes, statuses }
}

/**
 * @public
 *
 * Builds a best-effort default TaskType -> TaskType mapping between two ProjectTypes, matched by
 * exact name, falling back to kind+ofClass when there is exactly one candidate. Entries that can't
 * be confidently matched are left out of the map for the user to fill in manually.
 */
export function buildDefaultTaskTypeMap (
  oldTaskTypes: TaskType[],
  newTaskTypes: TaskType[]
): Map<Ref<TaskType>, Ref<TaskType>> {
  const result = new Map<Ref<TaskType>, Ref<TaskType>>()
  for (const oldType of oldTaskTypes) {
    const byName = newTaskTypes.find((it) => it.name.trim().toLowerCase() === oldType.name.trim().toLowerCase())
    if (byName !== undefined) {
      result.set(oldType._id, byName._id)
      continue
    }
    const byKind = newTaskTypes.filter((it) => it.kind === oldType.kind && it.ofClass === oldType.ofClass)
    if (byKind.length === 1) {
      result.set(oldType._id, byKind[0]._id)
    }
  }
  return result
}

/**
 * @public
 *
 * Builds a best-effort default Status -> Status mapping, scoped per already-mapped TaskType pair.
 * Prefers matching by StatusCategory (tie-broken by category order), falls back to name match.
 * Statuses that can't be confidently matched are left out of the map for the user to fill in manually.
 */
export function buildDefaultStatusMap (
  oldStatuses: Status[],
  oldTaskTypes: TaskType[],
  newTaskTypes: TaskType[],
  newStatusesById: IdMap<Status>,
  taskTypeMap: Map<Ref<TaskType>, Ref<TaskType>>,
  categories: IdMap<StatusCategory>
): Map<Ref<Status>, Ref<Status>> {
  const result = new Map<Ref<Status>, Ref<Status>>()
  for (const oldStatus of oldStatuses) {
    const oldTaskType = oldTaskTypes.find((it) => it.statuses.includes(oldStatus._id))
    const newTaskTypeId = oldTaskType !== undefined ? taskTypeMap.get(oldTaskType._id) : undefined
    const newTaskType = newTaskTypeId !== undefined ? newTaskTypes.find((it) => it._id === newTaskTypeId) : undefined
    if (newTaskType === undefined) continue

    const candidates = newTaskType.statuses
      .map((id) => newStatusesById.get(id))
      .filter((it): it is Status => it !== undefined)

    const byCategory = oldStatus.category !== undefined ? candidates.filter((it) => it.category === oldStatus.category) : []
    const byName = candidates.find((it) => it.name.trim().toLowerCase() === oldStatus.name.trim().toLowerCase())

    if (byCategory.length === 1) {
      result.set(oldStatus._id, byCategory[0]._id)
    } else if (byName !== undefined) {
      result.set(oldStatus._id, byName._id)
    } else if (byCategory.length > 1) {
      const sorted = [...byCategory].sort(
        (a, b) =>
          (categories.get(a.category as Ref<StatusCategory>)?.order ?? 0) -
          (categories.get(b.category as Ref<StatusCategory>)?.order ?? 0)
      )
      result.set(oldStatus._id, sorted[0]._id)
    }
  }
  return result
}

const REMAP_CHUNK_SIZE = 200

/**
 * @public
 *
 * Bulk-updates every Task/Issue in a project to point at the new TaskType/Status refs after a
 * project's type has changed. Chunked into batches of REMAP_CHUNK_SIZE, each committed as its own
 * transaction (following the apply/commit pattern used for bulk status updates elsewhere), so a
 * large project doesn't produce one oversized transaction.
 */
export async function remapProjectIssues (
  client: TxOperations,
  project: Ref<Project>,
  taskTypeMap: Map<Ref<TaskType>, Ref<TaskType>>,
  statusMap: Map<Ref<Status>, Ref<Status>>
): Promise<void> {
  const docs = await client.findAll(task.class.Task, { space: project })
  for (let i = 0; i < docs.length; i += REMAP_CHUNK_SIZE) {
    const chunk = docs.slice(i, i + REMAP_CHUNK_SIZE)
    const ops = client.apply(undefined, 'remap-project-type')
    for (const doc of chunk) {
      const newKind = taskTypeMap.get(doc.kind)
      const newStatus = statusMap.get(doc.status)
      const update: DocumentUpdate<Task> = {}
      if (newKind !== undefined && newKind !== doc.kind) {
        update.kind = newKind
      }
      if (newStatus !== undefined && newStatus !== doc.status) {
        update.status = newStatus
      }
      if (Object.keys(update).length > 0) {
        await ops.update(doc, update)
      }
    }
    await ops.commit()
  }
}

/**
 * @public
 */
export async function createState<T extends Status> (
  client: TxOperations,
  _class: Ref<Class<T>>,
  data: Data<T>
): Promise<Ref<T>> {
  const query: DocumentQuery<Status> = { name: data.name, ofAttribute: data.ofAttribute }
  if (data.category !== undefined) {
    query.category = data.category
  }
  const exists = await client.findOne(_class, query)
  if (exists !== undefined) {
    return exists._id as Ref<T>
  }
  const res = await client.createDoc(_class, core.space.Model, data)
  return res
}

/**
 * @public
 */
export function calculateStatuses (
  projectType: { statuses: ProjectType['statuses'], tasks: ProjectType['tasks'] },
  taskTypes: Map<Ref<TaskType>, Data<TaskType>>,
  override: Array<{ taskTypeId: Ref<TaskType>, statuses: Array<Ref<Status>> }>
): ProjectStatus[] {
  const stIds = new Map<Ref<Status>, ProjectStatus>()
  for (const s of projectType.statuses) {
    if (!stIds.has(s._id)) {
      stIds.set(s._id, s)
    }
  }
  const processed = new Set<string>()
  const result: ProjectStatus[] = []

  for (const tt of projectType.tasks) {
    const statusesList = override.find((it) => it.taskTypeId === tt)?.statuses ?? taskTypes.get(tt)?.statuses ?? []
    for (const tts of statusesList) {
      const prjStatus = stIds.get(tts)
      const key = `${tts}:${tt}`
      if (!processed.has(key)) {
        processed.add(key)
        result.push({ ...(prjStatus ?? {}), _id: tts, taskType: tt })
      }
    }
  }
  return result
}

/**
 * @public
 */
export function findStatusAttr (h: Hierarchy, _class: Ref<Class<Task>>): AnyAttribute {
  const attrs = h.getAllAttributes(_class)
  for (const it of attrs.values()) {
    if (it.type._class === core.class.RefTo && h.isDerived((it.type as RefTo<any>).to, core.class.Status)) {
      return it
    }
  }
  return h.getAttribute(task.class.Task, 'status')
}

export type TaskTypeWithFactory = Omit<Data<TaskType>, 'statuses' | 'parent' | 'targetClass'> & {
  _id: TaskType['_id']
  factory: Data<Status>[]
} & Partial<Pick<TaskType, 'targetClass'>>

type ProjectData = Omit<Data<ProjectType>, 'statuses' | 'targetClass'>

async function createStates (
  client: TxOperations,
  states: Data<Status>[],
  stateClass: Ref<Class<Status>>
): Promise<Ref<Status>[]> {
  const statuses: Ref<Status>[] = []
  for (const st of states) {
    statuses.push(await createState(client, stateClass, st))
  }
  return statuses
}

/**
 * @public
 */
export async function createProjectType (
  client: TxOperations,
  data: ProjectData,
  tasks: TaskTypeWithFactory[],
  _id: Ref<ProjectType>
): Promise<Ref<ProjectType>> {
  const current = await client.findOne(task.class.ProjectType, { _id })
  if (current !== undefined) {
    return current._id
  }

  const _tasks: Ref<TaskType>[] = []
  const tasksData = new Map<Ref<TaskType>, Data<TaskType>>()
  const _statues = new Set<Ref<Status>>()

  const categoryObj = client.getModel().findObject(data.descriptor)
  if (categoryObj === undefined) {
    throw new Error('category is not found in model')
  }

  await createTaskTypes(tasks, _id, client, _statues, tasksData, _tasks, false)

  const baseClassClass = client.getHierarchy().getClass(categoryObj.baseClass)

  // NOTE: it is important for this id to be consistent when re-creating the same
  // project type with the same id as it will happen during every migration if type is created by the system
  const targetProjectClassId = `${_id}:type:mixin` as Ref<Class<Doc>>
  const tmpl = await client.createDoc(
    task.class.ProjectType,
    core.space.Model,
    {
      description: data.description,
      shortDescription: data.shortDescription,
      descriptor: data.descriptor,
      roles: 0,
      tasks: _tasks,
      name: data.name,
      statuses: calculateStatuses({ tasks: _tasks, statuses: [] }, tasksData, []),
      targetClass: targetProjectClassId,
      classic: data.classic
    },
    _id
  )

  // Mixin to hold custom fields of this project type
  await client.createDoc(
    core.class.Mixin,
    core.space.Model,
    {
      extends: categoryObj.baseClass,
      kind: ClassifierKind.MIXIN,
      label: getEmbeddedLabel(data.name),
      icon: baseClassClass.icon
    },
    targetProjectClassId
  )

  // TODO: not needed ???
  await client.createMixin(targetProjectClassId, core.class.Mixin, core.space.Model, task.mixin.ProjectTypeClass, {
    projectType: _id
  })

  return tmpl
}

/**
 * @public
 */
export async function updateProjectType (
  client: TxOperations,
  projectType: Ref<ProjectType>,
  tasks: TaskTypeWithFactory[]
): Promise<void> {
  const current = await client.findOne(task.class.ProjectType, { _id: projectType })
  if (current === undefined) {
    throw new PlatformError(unknownStatus('No project type found'))
  }

  const _tasks: Ref<TaskType>[] = [...current.tasks]
  const tasksData = new Map<Ref<TaskType>, Data<TaskType>>()
  const _statues = new Set<Ref<Status>>()

  const hasUpdates = await createTaskTypes(tasks, projectType, client, _statues, tasksData, _tasks, true)

  if (hasUpdates) {
    const ttypes = await client.findAll<TaskType>(task.class.TaskType, { _id: { $in: _tasks } })
    const newStatuses = calculateStatuses(
      {
        statuses: current.statuses,
        tasks: _tasks
      },
      new Map(ttypes.map((it) => [it._id, it])),
      []
    )
    await client.update(current, {
      tasks: _tasks,
      statuses: newStatuses
    })
  }
}

async function createTaskTypes (
  tasks: TaskTypeWithFactory[],
  _id: Ref<ProjectType>,
  client: TxOperations,
  _statues: Set<Ref<Status>>,
  tasksData: Map<Ref<TaskType>, Data<TaskType>>,
  _tasks: Ref<TaskType>[],
  skipExisting: boolean
): Promise<boolean> {
  const existingTaskTypes = await client.findAll(task.class.TaskType, { parent: _id })

  let hasUpdates = false
  for (const it of tasks) {
    const { factory, _id: taskId, ...data } = it

    if (skipExisting) {
      const existingOne = existingTaskTypes.find((tt) => tt.ofClass === data.ofClass)
      if (existingOne !== undefined) {
        // We have similar one, let's check categories
        continue
      }
    }
    hasUpdates = true

    const statuses = await createStates(client, factory, data.statusClass)
    for (const st of statuses) {
      _statues.add(st)
    }
    const tdata = {
      ...data,
      parent: _id,
      statuses
    }

    const ofClassClass = client.getHierarchy().getClass(data.ofClass)

    tdata.icon = ofClassClass.icon

    if (tdata.targetClass === undefined) {
      // Create target class for custom field.
      // NOTE: it is important for this id to be consistent when re-creating the same
      // task type with the same id as it will happen during every migration if type is created by the system
      const targetClassId = `${taskId}:type:mixin` as Ref<Class<Task>>
      tdata.targetClass = targetClassId

      await client.createDoc(
        core.class.Mixin,
        core.space.Model,
        {
          extends: data.ofClass,
          kind: ClassifierKind.MIXIN,
          label: ofClassClass.label,
          icon: ofClassClass.icon
        },
        targetClassId
      )

      await client.createMixin(targetClassId, core.class.Mixin, core.space.Model, task.mixin.TaskTypeClass, {
        taskType: taskId,
        projectType: _id
      })
    }
    await client.createDoc(task.class.TaskType, core.space.Model, tdata as Data<TaskType>, taskId)
    tasksData.set(taskId, tdata as Data<TaskType>)
    _tasks.push(taskId)
  }
  return hasUpdates
}
